import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, 
  User, 
  Building2, 
  FileText, 
  Mail, 
  ChevronRight,
  Loader2,
  RefreshCw,
  Maximize2,
  Settings,
  DownloadCloud
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ExtractedDataPanel from "@/components/ExtractedDataPanel";
import CRMStatusBanner from "@/components/CRMStatusBanner";
import ContactForm from "@/components/forms/ContactForm";
import OpportunityForm from "@/components/forms/OpportunityForm";
import QuoteForm from "@/components/forms/QuoteForm";
import TemplateSelector from "@/components/TemplateSelector";
import CreateTaskButton from "@/components/CreateTaskButton";
import UserMenu from "@/components/UserMenu";
import SettingsPage from "@/components/SettingsPage";

// Manual sync button for the local Sellsy opps index — pulls the 25 most
// recent opportunities from Sellsy and upserts them so they become findable
// in the contact lookup (workaround for Sellsy V2 search filter bugs).
const SyncOppsButton = () => {
  const [syncing, setSyncing] = useState(false);
  const handleClick = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/opportunities/index/sync`
      );
      const d = r.data || {};
      const total = (d.inserted || 0) + (d.skipped || 0);
      toast.success(
        `Sync OK : ${total} opportunités vérifiées${d.inserted ? ` (${d.inserted} nouvelles)` : ""}`
      );
    } catch (_) {
      toast.error("Echec de la synchronisation Sellsy");
    } finally {
      setSyncing(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={syncing}
      className="h-6 w-6 p-0"
      title="Synchroniser les opportunités récentes depuis Sellsy"
      data-testid="sync-opps-btn"
    >
      {syncing ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
      ) : (
        <DownloadCloud className="w-3.5 h-3.5 text-slate-500" />
      )}
    </Button>
  );
};

const Sidebar = ({
  email,
  extractedData,
  loading,
  crmStatus,
  animations,
  templates,
  onExtract,
  onUpdateExtractedData,
  apiUrl,
  user,
  createdContact,
  createdCompany,
  createdOpportunity,
  createdQuote,
  setCreatedContact,
  setCreatedCompany,
  setCreatedOpportunity,
  setCreatedQuote,
  isOutlookContext,
  reloadOutlookEmail,
  openOutlookPopout,
  onRefreshCRMStatus,
  onInsertTrame,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(380);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(380);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.currentTarget.classList.add('dragging');

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newWidth = Math.min(700, Math.max(300, startWidth.current + diff));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.querySelectorAll('.sidebar-resize-handle').forEach(el => el.classList.remove('dragging'));
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth]);

  // If settings page is open
  if (showSettings) {
    return (
      <aside className="sidebar" style={{ '--sidebar-width': `${sidebarWidth}px` }} data-testid="sidebar">
        <div className="sidebar-resize-handle" onMouseDown={handleMouseDown} data-testid="resize-handle" />
        <SettingsPage
          apiUrl={apiUrl}
          onClose={() => setShowSettings(false)}
          user={user}
        />
      </aside>
    );
  }

  return (
    <aside className="sidebar" style={{ '--sidebar-width': `${sidebarWidth}px` }} data-testid="sidebar">
      <div className="sidebar-resize-handle" onMouseDown={handleMouseDown} data-testid="resize-handle" />
      {/* Header */}
      <div className="sidebar-header">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
            Commercial Mail Sync
          </h1>
          <div className="flex items-center gap-1">
            <span className="badge badge-info">Sellsy</span>
            <SyncOppsButton />
            {isOutlookContext && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openOutlookPopout}
                  className="h-6 w-6 p-0"
                  title="Agrandir dans une fenêtre séparée redimensionnable"
                  data-testid="outlook-popout-btn"
                >
                  <Maximize2 className="w-3.5 h-3.5 text-slate-500" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={reloadOutlookEmail}
                  className="h-6 w-6 p-0"
                  title="Recharger l'email depuis Outlook"
                  data-testid="outlook-reload-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              className="h-6 w-6 p-0"
              title="Réglages"
              data-testid="settings-btn"
            >
              <Settings className="w-3.5 h-3.5 text-slate-500" />
            </Button>
          </div>
        </div>
        
        {/* Sender Info - only show when email parsed */}
        {email.sender_email ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-medium">
              {email.sender_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-sender-name">
                {email.sender_name || 'Inconnu'}
              </p>
              <p className="text-xs text-slate-500 truncate" data-testid="sidebar-sender-email">
                {email.sender_email}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">Collez un email pour commencer</p>
        )}
      </div>

      {/* Main Content */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {/* Extract Button */}
          {!extractedData && (
            <Button
              onClick={onExtract}
              disabled={loading}
              className="w-full h-9 mb-3 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium"
              data-testid="extract-email-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Analyser l'email
                </>
              )}
            </Button>
          )}

          {/* CRM Status Banner */}
          {extractedData && (
            <CRMStatusBanner 
              contactStatus={crmStatus.contact} 
              companyStatus={crmStatus.company}
              onRefresh={onRefreshCRMStatus}
            />
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full h-8 bg-slate-100 p-0.5 rounded-md mb-3 grid grid-cols-3">
              <TabsTrigger 
                value="overview" 
                className="h-7 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900"
                data-testid="tab-overview"
              >
                Aperçu
              </TabsTrigger>
              <TabsTrigger 
                value="actions" 
                className="h-7 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900"
                data-testid="tab-actions"
              >
                Actions
              </TabsTrigger>
              <TabsTrigger 
                value="templates" 
                className="h-7 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900"
                data-testid="tab-templates"
              >
                Modèles
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab — forceMount so user state (expanded forms, picker selections)
                is preserved across tab switches */}
            <TabsContent value="overview" className="mt-0 data-[state=inactive]:hidden" forceMount>
              {/* Standalone-only helper: insert a blank request skeleton into the
                  email zone with every Aperçu field title (manual entry). */}
              {!isOutlookContext && onInsertTrame && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onInsertTrame}
                  className="w-full h-8 mb-3 text-xs font-medium border-slate-200"
                  data-testid="insert-trame-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  Trame de demande
                </Button>
              )}
              {extractedData ? (
                <ExtractedDataPanel 
                  data={extractedData}
                  onUpdate={onUpdateExtractedData}
                  animations={animations}
                />
              ) : (
                <div className="empty-state">
                  <Sparkles className="empty-state-icon mx-auto" />
                  <p className="empty-state-title">Aucune donnée extraite</p>
                  <p className="empty-state-text">
                    Cliquez sur "Analyser l'email" pour extraire les informations commerciales.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Actions Tab — forceMount preserves ActionCard `expanded` state */}
            <TabsContent value="actions" className="mt-0 space-y-2 data-[state=inactive]:hidden" forceMount>
              {extractedData ? (
                <>
                  {/* Quick Actions */}
                  <div className="section-header">Actions rapides</div>
                  
                  <ActionCard
                    icon={User}
                    title="Créer un contact"
                    description={crmStatus.contact ? "Contact existant trouvé" : "Nouveau contact"}
                    status={crmStatus.contact ? "found" : "new"}
                    testId="action-create-contact"
                  >
                    <ContactForm 
                      extractedData={extractedData}
                      existingContact={crmStatus.contact}
                      existingCompany={crmStatus.company}
                      apiUrl={apiUrl}
                      createdContact={createdContact}
                      createdCompany={createdCompany}
                      onContactCreated={setCreatedContact}
                      onCompanyCreated={setCreatedCompany}
                    />
                  </ActionCard>

                  <ActionCard
                    icon={Building2}
                    title="Créer une opportunité"
                    description="Nouvelle affaire commerciale"
                    testId="action-create-opportunity"
                  >
                  <OpportunityForm 
                    extractedData={extractedData}
                    animations={animations}
                    apiUrl={apiUrl}
                    existingCompany={crmStatus.company}
                    existingContact={crmStatus.contact}
                    createdContact={createdContact}
                    createdCompany={createdCompany}
                    createdOpportunity={createdOpportunity}
                    onOpportunityCreated={setCreatedOpportunity}
                    onUpdateExtractedData={onUpdateExtractedData}
                  />
                  </ActionCard>

                  <ActionCard
                    icon={FileText}
                    title="Créer un devis"
                    description="Générer une proposition"
                    testId="action-create-quote"
                  >
                    <QuoteForm 
                      extractedData={extractedData}
                      animations={animations}
                      apiUrl={apiUrl}
                      createdOpportunity={createdOpportunity}
                      createdContact={createdContact}
                      createdCompany={createdCompany}
                      existingContact={crmStatus.contact}
                      existingCompany={crmStatus.company}
                      createdQuote={createdQuote}
                      onQuoteCreated={setCreatedQuote}
                    />
                  </ActionCard>

                  <CreateTaskButton
                    createdQuote={createdQuote}
                    createdOpportunity={createdOpportunity}
                  />
                </>
              ) : (
                <div className="empty-state">
                  <FileText className="empty-state-icon mx-auto" />
                  <p className="empty-state-title">Actions non disponibles</p>
                  <p className="empty-state-text">
                    Analysez d'abord l'email pour accéder aux actions CRM.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Templates Tab — forceMount preserves any in-progress template selection */}
            <TabsContent value="templates" className="mt-0 data-[state=inactive]:hidden" forceMount>
              <TemplateSelector 
                templates={templates}
                extractedData={extractedData}
                senderEmail={email.sender_email}
                apiUrl={apiUrl}
                isOutlookContext={isOutlookContext}
                createdQuote={createdQuote}
              />
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
      
      {/* User Menu at bottom */}
      <UserMenu />
    </aside>
  );
};

// Action Card Component
const ActionCard = ({ icon: Icon, title, description, status, testId, children }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card" data-testid={testId}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 text-left"
        data-testid={`${testId}-toggle`}
      >
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
          status === 'found' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
        }`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
